import * as React from 'react'
import {Accordion, Box, List, ListItem, ListItemText, Loader, Typography,} from 'cs-kevlar-react'
import {ListItemIcon} from '@mui/material'
import PowerIcon from '@mui/icons-material/Power'
import PowerOffIcon from '@mui/icons-material/PowerOff'
import {CustomerInterface} from 'contexts/CurrentCustomerContext'
import {createStyles, makeStyles, Theme,} from 'cs-kevlar-react/lib/es/theming/theme'
import {KeyValueText} from 'components/KeyValueText'
import {useCustomerApi, usePageNotifications} from 'hooks'
import {CertificateInfo, InterfaceHealth, InterfaceState, OperationEvent,} from '../../api'
import {serverDateToLocaleDateTimeString} from 'utils'
import {getInterfaceStateLabel} from 'utils/enums'

type RevocableInterfaceHealth = InterfaceHealth & {
  certificate: CertificateInfo | null
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    item: {
      '& + &': {
        marginTop: theme.spacing(8),
      },
    },
    fingerprint: {
      wordBreak: 'break-all',
    },
    content: {
      padding: theme.spacing(8),
    },
    connectionStateRoot: {
      width: '100%',
    },
    connectionStateList: {
      padding: 0,
    },
    connectionStateListItem: {
      display: 'flex',
      padding: theme.spacing(0, 0, 0, 4),
    },
    connectionStateSecondaryContainer: {
      display: 'flex',
    },
    connectionStateTimestamp: {
      flex: 1,
    },
    connectionStateDuration: {
      flex: 'none',
    },
    connectionStateIcon: {
      minWidth: '44px',
    },
    connectionStateEventLimit: {
      marginTop: theme.spacing(8),
    },
    center: {
      display: 'flex',
      justifyContent: 'center',
      flexGrow: 1,
      minWidth: 0,
    },
  }),
)

const formatDateTime = (value: string) => {
  const timestamp = new Date(value)
  const formatted =
    timestamp.toLocaleDateString('en-US') +
    ' ' +
    timestamp.toLocaleTimeString('en-US')

  return formatted
}

const buildOperationStateValue = (
  lastOpAt: string,
  wasLastOpSuccessful: boolean,
  lastOpDuration: any,
) => {
  const lastOpSuccess = wasLastOpSuccessful ? 'succeeded' : 'failed'
  const lastOpTimeString = formatDateTime(lastOpAt)
  return `${lastOpTimeString} ${lastOpSuccess} after ${lastOpDuration} seconds`
}

const parseTimeSpanToSeconds = (timeSpan: any) => {
  const timePieces = timeSpan.split(':')
  const hours = parseInt(timePieces[0], 10)
  const minutes = parseInt(timePieces[1], 10)
  const seconds = parseFloat(timePieces[2])
  return hours * 60 * 60 + minutes * 60 + seconds
}

const buildRequestDetails = (key: string, data: OperationEvent) => {
  const duration = parseTimeSpanToSeconds(data.averageTime)
  return `${key} - ${data.count} requests with average duration of ${duration} seconds`
}

const isLongRunningRequest = (key: string, data: OperationEvent) => {
  const duration = parseTimeSpanToSeconds(data.averageTime)
  return duration > 2
}

interface ConnectorHealthProps {
  connector: CustomerInterface
  className?: string
}

export const ConnectorHealth: React.FunctionComponent<ConnectorHealthProps> = ({
  connector,
  className,
}) => {
  const { getHealthInfoForCustomerInterface } = useCustomerApi()
  const { dispatch: dispatchNotification } = usePageNotifications()
  const classes = useStyles({})

  const [refresh, setRefresh] = React.useState<number>(Date.now())
  const [isLoading, setIsLoading] = React.useState<boolean>(true)

  const [
    connectorHealth,
    setConnectorHealth,
  ] = React.useState<RevocableInterfaceHealth | null>(null)

  React.useEffect(() => {
    const loadData = async () => {
      if (!connector.customerAgencyId || !connector.customerInterfaceId) return

      setIsLoading(true)

      try {
        const response = await getHealthInfoForCustomerInterface(
          connector.customerAgencyId,
          connector.customerInterfaceId,
        )

        if (!response) return

        if (response.status === 200 && response.data) {
          setConnectorHealth(response.data)
        } else {
          dispatchNotification({
            type: 'SetError',
            payload:
              'There was an error loading the health information for the connector.',
          })
        }
      } finally {
        setIsLoading(false)
      }
    }

    loadData()

    const timeout = setTimeout(() => setRefresh(Date.now()), 30000)

    return () => {
      clearTimeout(timeout)
    }
  }, [refresh])

  if (isLoading && connectorHealth === null) {
    return <Loader size="sm" text="Loading health information" />
  }

  if (connectorHealth === null) {
    return (
      <Box className={classes.content}>
        <Typography>
          Unable to retrieve connector health data. Please ensure the connector
          is running and connected.
        </Typography>
      </Box>
    )
  }

  return (
    <Box>
      <Accordion
        id="ConnectorHealth::Summary"
        title="Summary"
        initiallyExpanded
      >
        <Box>
          <KeyValueText className={classes.item} label="Customer Agency Id">
            {connectorHealth.customerAgencyId}
          </KeyValueText>
          <KeyValueText className={classes.item} label="Connection Id">
            {connectorHealth.connectionId}
          </KeyValueText>
          <KeyValueText
            className={classes.item}
            label="Connector info last updated at"
          >
            {serverDateToLocaleDateTimeString(connectorHealth.updatedAt)}
          </KeyValueText>
          <KeyValueText className={classes.item} label="Status">
            {connectorHealth.statusMessage}
          </KeyValueText>
          <KeyValueText className={classes.item} label="Version">
            {connectorHealth.version}
          </KeyValueText>
          <KeyValueText className={classes.item} label="Abstraction Version">
            {connectorHealth.abstractionVersion}
          </KeyValueText>
          <KeyValueText className={classes.item} label="API Version">
            {connectorHealth.apiVersion}
          </KeyValueText>
          <KeyValueText className={classes.item} label="SSL">
            {!connectorHealth.sslInfo && "N/A"}
            {!!connectorHealth.sslInfo && `${connectorHealth.sslInfo.protocol}, ${connectorHealth.sslInfo.cipher}`}
          </KeyValueText>
          <KeyValueText className={classes.item} label="Enabled Features">
            {(connectorHealth.enabledFeatures ?? []).join(', ')}
          </KeyValueText>
          {connectorHealth.certificate !== null && (
            <KeyValueText
              className={classes.item}
              label="Current Certificate Info"
            >
              {() => (
                <React.Fragment>
                  <KeyValueText label="Issued On:" orientation="horizontal">
                    {connectorHealth.certificate.issuedOn}
                  </KeyValueText>
                  <KeyValueText label="Expires On:" orientation="horizontal">
                    {connectorHealth.certificate.expiresOn}
                  </KeyValueText>
                  <KeyValueText
                    label="Revocation Status:"
                    orientation="horizontal"
                  >
                    {connectorHealth.certificate.revocationStatus}
                  </KeyValueText>
                  <KeyValueText
                    label="Fingerprint:"
                    orientation="horizontal"
                    valueClassName={classes.fingerprint}
                  >
                    {connectorHealth.certificate.fingerprint}
                  </KeyValueText>
                </React.Fragment>
              )}
            </KeyValueText>
          )}
        </Box>
      </Accordion>
      <Accordion
        id="ConnectorHealth::ConnectionHistory"
        title="Connection History"
        initiallyExpanded
      >
        <Box className={classes.connectionStateRoot}>
          {!connectorHealth.connectionStateHistory && (
            <Typography>No events available.</Typography>
          )}

          {!!connectorHealth.connectionStateHistory && (
            <List className={classes.connectionStateList}>
              {connectorHealth.connectionStateHistory.map(
                (stateChange, index) => (
                  <ListItem
                    key={'ConnectionStatusHistoryItem' + index}
                    className={classes.connectionStateListItem}
                  >
                    <ListItemIcon className={classes.connectionStateIcon}>
                      {stateChange.newState.valueOf() ==
                        InterfaceState.Connected.valueOf() && <PowerIcon />}

                      {stateChange.newState.valueOf() ==
                        InterfaceState.Disconnected.valueOf() && (
                        <PowerOffIcon color="secondary" />
                      )}
                    </ListItemIcon>
                    <ListItemText
                      primary={getInterfaceStateLabel(stateChange.newState)}
                      secondary={
                        <Box
                          className={classes.connectionStateSecondaryContainer}
                        >
                          <Typography
                            className={classes.connectionStateTimestamp}
                            variant="body2"
                          >
                            {formatDateTime(stateChange.occurredOn)}
                          </Typography>

                          <Typography
                            className={classes.connectionStateDuration}
                            variant="body2"
                          >
                            {stateChange.durationDescription}
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItem>
                ),
              )}

              <Box className={classes.center}>
                <Typography
                  className={classes.connectionStateEventLimit}
                  variant="body2"
                  color="textSecondary"
                >
                  Showing up to 10 events
                </Typography>
              </Box>
            </List>
          )}
        </Box>
      </Accordion>
      <Accordion
        id="ConnectorHealth::Details"
        title="Details"
        initiallyExpanded
      >
        <Box>
          {!!connectorHealth.operationState &&
            Object.keys(connectorHealth.operationState)
              .sort()
              .map((operationType) => (
                <KeyValueText
                  key={operationType}
                  label={operationType}
                  className={classes.item}
                >
                  {() => (
                    <React.Fragment>
                      <KeyValueText
                        label="Last Operation"
                        orientation="horizontal"
                      >
                        {buildOperationStateValue(
                          connectorHealth.operationState[operationType]
                            .lastOpAt,
                          connectorHealth.operationState[operationType]
                            .wasLastOpSuccessful,
                          connectorHealth.operationState[operationType]
                            .lastOpDuration,
                        )}
                      </KeyValueText>
                      {!connectorHealth.operationState[operationType]
                        .wasLastOpSuccessful && (
                        <KeyValueText
                          label="Last Successful Operation At"
                          orientation="horizontal"
                        >
                          {
                            connectorHealth.operationState[operationType]
                              .lastSuccessfulOpWasAt
                          }
                        </KeyValueText>
                      )}
                      {Object.keys(
                        connectorHealth.operationState[operationType]
                          .failureCount,
                      ).map((failedKey) => (
                        <KeyValueText
                          key={`ConnectorHealth::Operations::${operationType}::${failedKey}`}
                          label="Failed"
                        >
                          {buildRequestDetails(
                            failedKey,
                            connectorHealth.operationState[operationType]
                              .failureCount[failedKey],
                          )}
                        </KeyValueText>
                      ))}
                      {Object.keys(
                        connectorHealth.operationState[operationType]
                          .successCount,
                      )
                        .filter((successKey) =>
                          isLongRunningRequest(
                            successKey,
                            connectorHealth.operationState[operationType]
                              .successCount[successKey],
                          ),
                        )
                        .map((successKey) => (
                          <KeyValueText
                            key={`ConnectorHealth::Operations::${operationType}::${successKey}`}
                            label="Slow"
                          >
                            {buildRequestDetails(
                              successKey,
                              connectorHealth.operationState[operationType]
                                .successCount[successKey],
                            )}
                          </KeyValueText>
                        ))}
                    </React.Fragment>
                  )}
                </KeyValueText>
              ))}
        </Box>
      </Accordion>
    </Box>
  )
}
